import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import {
  Close as CloseIcon,
  GetApp as DownloadIcon,
  Email as EmailIcon,
  DateRange as DateRangeIcon,
  Code as HtmlIcon,
} from '@material-ui/icons';

import type { PreviewResult, ReportType } from '../types';
import { formatBytes, getReportTypeLabel } from '../utils';

const useStyles = makeStyles(theme => ({
  previewDialog: {
    '& .MuiDialog-paper': {
      maxWidth: 900,
      width: '100%',
    },
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  previewSection: {
    marginBottom: theme.spacing(2),
  },
  previewSectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  previewEmailDetails: {
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  previewEmailRow: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
    '& strong': {
      minWidth: 100,
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(1),
    },
    '& span': {
      color: theme.palette.text.primary,
    },
  },
  previewRecipients: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  previewPdfFrame: {
    width: '100%',
    height: 500,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  previewHtmlFrame: {
    width: '100%',
    height: 400,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  previewTabs: {
    marginBottom: theme.spacing(2),
  },
  csvContent: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    maxHeight: 400,
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
}));

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  preview: PreviewResult | null;
  loading: boolean;
  orgName: string;
  reportType: ReportType;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({ open, onClose, preview, loading, orgName, reportType }) => {
  const classes = useStyles();
  const [previewTab, setPreviewTab] = useState(0);

  const handleDownloadPdf = () => {
    if (!preview?.pdfBase64) return;
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${preview.pdfBase64}`;
    link.download = preview.filename || 'report.pdf';
    link.click();
  };

  const handleDownloadCsv = () => {
    if (!preview?.csvContent) return;
    const blob = new Blob([preview.csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = preview.filename || 'report.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const pdfUrl = preview?.pdfBase64 ? `data:application/pdf;base64,${preview.pdfBase64}` : null;
  const normalizedReportType = reportType.toLowerCase();
  const isCsvReport = normalizedReportType === 'daily_csv';

  return (
    <Dialog open={open} onClose={onClose} className={classes.previewDialog} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box className={classes.previewHeader}>
          <Typography variant="h6">
            Preview: {orgName} - {getReportTypeLabel(reportType)} Report
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <LoadingState />
        ) : !preview ? (
          <Typography color="error">No preview data available</Typography>
        ) : !preview.success ? (
          <Alert severity="error">{preview.message || 'Failed to generate preview'}</Alert>
        ) : (
          <>
            <EmailDetailsSection preview={preview} />
            <DateRangeSection preview={preview} />
            <Divider style={{ margin: '16px 0' }} />

            {isCsvReport ? (
              <CsvPreview preview={preview} />
            ) : (
              <PdfPreview preview={preview} pdfUrl={pdfUrl} previewTab={previewTab} onTabChange={setPreviewTab} />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isCsvReport && preview?.csvContent && (
          <Button startIcon={<DownloadIcon />} onClick={handleDownloadCsv} color="primary">
            Download CSV
          </Button>
        )}
        {!isCsvReport && preview?.pdfBase64 && (
          <Button startIcon={<DownloadIcon />} onClick={handleDownloadPdf} color="primary">
            Download PDF
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const LoadingState: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
    <CircularProgress />
    <Typography style={{ marginLeft: 16 }}>Generating preview...</Typography>
  </Box>
);

interface EmailDetailsSectionProps {
  preview: PreviewResult;
}

const EmailDetailsSection: React.FC<EmailDetailsSectionProps> = ({ preview }) => {
  const classes = useStyles();

  return (
    <Box className={classes.previewSection}>
      <Typography variant="subtitle2" className={classes.previewSectionTitle}>
        <EmailIcon fontSize="small" />
        Email Details
      </Typography>
      <Box className={classes.previewEmailDetails}>
        <Box className={classes.previewEmailRow}>
          <strong>Subject:</strong>
          <span>{preview.emailSubject || '-'}</span>
        </Box>
        <Box className={classes.previewEmailRow}>
          <strong>From:</strong>
          <span>{preview.emailFrom || '-'}</span>
        </Box>
        <Box className={classes.previewEmailRow}>
          <strong>To:</strong>
          <Box className={classes.previewRecipients}>
            {preview.emailRecipients && preview.emailRecipients.length > 0 ? (
              preview.emailRecipients.map((email, i) => <Chip key={i} label={email} size="small" variant="outlined" />)
            ) : (
              <Typography variant="body2" color="textSecondary">
                No recipients configured
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

interface DateRangeSectionProps {
  preview: PreviewResult;
}

const DateRangeSection: React.FC<DateRangeSectionProps> = ({ preview }) => {
  const classes = useStyles();

  return (
    <Box className={classes.previewSection}>
      <Typography variant="subtitle2" className={classes.previewSectionTitle}>
        <DateRangeIcon fontSize="small" />
        Date Range
      </Typography>
      <Typography variant="body2">
        {preview.dateRangeDisplay || `${preview.dateRangeStart} to ${preview.dateRangeEnd}`}
      </Typography>
    </Box>
  );
};

interface CsvPreviewProps {
  preview: PreviewResult;
}

const CsvPreview: React.FC<CsvPreviewProps> = ({ preview }) => {
  const classes = useStyles();

  return (
    <>
      <Typography variant="subtitle2" className={classes.previewSectionTitle}>
        CSV Content ({preview.csvRowCount ?? 0} rows)
      </Typography>
      <Box component="pre" className={classes.csvContent}>
        {preview.csvContent || 'No CSV content available'}
      </Box>
    </>
  );
};

interface PdfPreviewProps {
  preview: PreviewResult;
  pdfUrl: string | null;
  previewTab: number;
  onTabChange: (tab: number) => void;
}

const PdfPreview: React.FC<PdfPreviewProps> = ({ preview, pdfUrl, previewTab, onTabChange }) => {
  const classes = useStyles();

  return (
    <>
      <Tabs
        value={previewTab}
        onChange={(_, v) => onTabChange(v)}
        className={classes.previewTabs}
        indicatorColor="primary"
      >
        <Tab label="PDF Preview" />
        <Tab label="HTML Source" icon={<HtmlIcon fontSize="small" />} />
      </Tabs>

      {previewTab === 0 && (
        <Box>
          {pdfUrl ? (
            <iframe src={pdfUrl} className={classes.previewPdfFrame} title="PDF Preview" />
          ) : (
            <Typography color="textSecondary">No PDF generated</Typography>
          )}
        </Box>
      )}

      {previewTab === 1 && (
        <Box>
          {preview.htmlContent ? (
            <iframe srcDoc={preview.htmlContent} className={classes.previewHtmlFrame} title="HTML Preview" sandbox="" />
          ) : (
            <Typography color="textSecondary">No HTML content available</Typography>
          )}
        </Box>
      )}

      {preview.sizeBytes && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
          PDF Size: {formatBytes(preview.sizeBytes)} | Filename: {preview.filename}
        </Typography>
      )}
    </>
  );
};
